import { useMutation, useQuery } from '@apollo/client';

import { faPencil, faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import classnames from '../../../core/utils/classnames.tsx';
import * as React from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { withIcon } from '../../../core/components/Icon.tsx';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import { CREATE_WEBHOOK, DELETE_WEBHOOK, GET_WEBHOOKS } from '../../queries.ts';
import {
    CUSTOMER_INTEGRATION_WEBHOOK_ADD_PATH,
    CUSTOMER_INTEGRATION_WEBHOOK_EDIT_PATH,
    CUSTOMER_INTEGRATION_WEBHOOK_PATH,
} from '../../constants.ts';
import { Controller, useForm } from 'react-hook-form';
import Button from '../../../core/components/Button.tsx';
import { Link, useNavigate, useOutletContext } from 'react-router-dom';
import { url } from '../../../core/utils/link.ts';
import FormField from '../../../core/components/FormField.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import Checkbox from '../../../core/components/Checkbox.tsx';
import Modal from '../../../core/components/Modal.tsx';
import { useToaster } from '../../../core/components/Toast.tsx';
import { Outlet } from 'react-router';
import Layout from '../../../core/components/Layout.tsx';
import Page from '../../../core/components/Page';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import { SecretField } from '../../../core/components/Secret.tsx';

interface WebhookFormModalOutletContext {
    integrations: any[];
    refetch: () => Promise<any>;
}

const AddIcon = withIcon(faPlus);
const EditIcon = withIcon(faPencil);
const DeleteIcon = withIcon(faTrashCan);

const EVENT_ACTIONS = [
    'assistance_needed',
    'assisted',
    'before_generate_output',
    'output_generated',
    'before_send_output',
    'output_sent',
    'finished',
] as const;

const FORM_DEFAULT_VALUES = {
    name: '',
    enabled: true,
    url: '',
    authType: '',
    authCredentials: '',
    authHeader: '',
    username: '',
    password: '',
    events: [] as string[],
    showUnfinishedData: false,
    orderChannelIds: [] as string[],
    orderConfirmationChannelIds: [] as string[],
    rfqChannelIds: [] as string[],
    invoiceChannelIds: [] as string[],
    deliveryNoteChannelIds: [] as string[],
};

const EventsField = ({ control, formErrors }: { control: any; formErrors: any }) => {
    const { t } = useTranslation('integrations');

    return (
        <Controller
            name="events"
            control={control}
            rules={{
                validate: (value) => value.length > 0,
            }}
            render={({ field }) => {
                const toggleAction = (action: (typeof EVENT_ACTIONS)[number]) => {
                    const current = field.value as string[];
                    const next = current.includes(action) ? current.filter((a) => a !== action) : [...current, action];
                    field.onChange(next);
                };

                return (
                    <div className="flex flex-col gap-1.5">
                        <span className="text-sm font-medium text-content-strong">
                            {t('webhook.form.eventActions')}
                        </span>
                        <div className="flex flex-col gap-1">
                            {EVENT_ACTIONS.map((action) => (
                                <label
                                    key={action}
                                    className="flex gap-2 items-center cursor-pointer text-sm text-content-medium"
                                >
                                    <Checkbox
                                        checked={(field.value as string[]).includes(action)}
                                        onChange={() => toggleAction(action)}
                                    />
                                    {t(`webhook.form.eventActionLabels.${action}`)}
                                </label>
                            ))}
                        </div>
                    </div>
                );
            }}
        />
    );
};

const WebhookFormModal = ({
    title,
    submitButtonLabel,
    onSubmit,
    onClose,
    integration,
}: {
    title: string;
    submitButtonLabel: string;
    onSubmit: (data: any) => void;
    onClose: () => void;
    integration?: any;
}) => {
    const { t } = useTranslation('integrations');

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                enabled: integration.enabled !== undefined ? integration.enabled : true,
                url: integration.url || '',
                authType: integration.authType || '',
                authCredentials: '',
                authHeader: integration.authHeader || '',
                username: integration.username || '',
                password: '',
                events: [
                    ...new Set(
                        (JSON.parse(integration.events || '[]') as string[]).map((e: string) => e.split('.').pop())
                    ),
                ],
                showUnfinishedData: integration.showUnfinishedData || false,
                orderChannelIds: integration.orderChannels?.map((c) => c.id) || [],
                orderConfirmationChannelIds: integration.orderConfirmationChannels?.map((c) => c.id) || [],
                rfqChannelIds: integration.rfqChannels?.map((c) => c.id) || [],
                invoiceChannelIds: integration.invoiceChannels?.map((c) => c.id) || [],
                deliveryNoteChannelIds: integration.deliveryNoteChannels?.map((c) => c.id) || [],
            });
        }
    }, [integration]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });
    const orderChannels = channelData?.orderProcessingChannels || [];
    const orderConfirmationChannels = channelData?.orderConfirmationProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];
    const invoiceChannels = channelData?.invoiceProcessingChannels || [];
    const deliveryNoteChannels = channelData?.deliveryNoteProcessingChannels || [];

    return (
        <Modal
            visible={true}
            onClose={onClose}
            title={title}
            buttons={
                <>
                    <Button type="button" asChild>
                        <Link to={url(CUSTOMER_INTEGRATION_WEBHOOK_PATH)}>{t('webhook.form.cancelButton')}</Link>
                    </Button>
                    <Button variant="primary" type="submit" form="modal-form">
                        {submitButtonLabel}
                    </Button>
                </>
            }
        >
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)} id="modal-form">
                <Controller
                    name="name"
                    control={form.control}
                    rules={{ required: true }}
                    render={({ field }) => (
                        <FormField label={t('webhook.form.name')} error={formErrors.name}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="enabled"
                    control={form.control}
                    render={({ field }) => (
                        <label className="flex gap-3 items-start cursor-pointer">
                            <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                            <div className="flex flex-col gap-1">
                                <span className="text-content-strong text-sm">{t('webhook.form.enabled')}</span>
                                <span className="text-content-medium text-xs">
                                    {t('webhook.form.enabledDescription')}
                                </span>
                            </div>
                        </label>
                    )}
                />

                <Controller
                    name="url"
                    control={form.control}
                    rules={{ required: true }}
                    render={({ field }) => (
                        <FormField label={t('webhook.form.url')} error={formErrors.url}>
                            <StringField {...field} placeholder="https://api.example.com/webhook" />
                        </FormField>
                    )}
                />

                <Controller
                    name="authType"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('webhook.form.authType')}>
                            <StringField {...field} placeholder="e.g. Bearer, JWT" />
                        </FormField>
                    )}
                />

                <Controller
                    name="authCredentials"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('webhook.form.authCredentials')}
                            helpText={integration?.authCredentials ? t('secretUpdateOnly') : undefined}
                        >
                            <SecretField
                                {...field}
                                secretName="authCredentials"
                                secretId={integration?.id}
                                defaultIsVisible={false}
                                placeholder={integration?.authCredentials ? '••••••••' : undefined}
                            />
                        </FormField>
                    )}
                />

                <Controller
                    name="authHeader"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('webhook.form.authHeader')}>
                            <StringField {...field} placeholder="Authorization" />
                        </FormField>
                    )}
                />

                <Controller
                    name="username"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('webhook.form.username')}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="password"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('webhook.form.password')}
                            helpText={integration?.password ? t('secretUpdateOnly') : undefined}
                        >
                            <SecretField
                                {...field}
                                secretName="password"
                                secretId={integration?.id}
                                defaultIsVisible={false}
                                placeholder={integration?.password ? '••••••••' : undefined}
                            />
                        </FormField>
                    )}
                />

                <div className="flex flex-col gap-3">
                    <span className="text-sm font-medium text-content-strong">{t('webhook.form.events')}</span>
                    {formErrors.events && (
                        <span className="text-xs text-content-danger-strong">{t('webhook.form.eventsRequired')}</span>
                    )}
                    <EventsField control={form.control} formErrors={formErrors} />
                </div>

                <Controller
                    name="showUnfinishedData"
                    control={form.control}
                    render={({ field }) => (
                        <label className="flex gap-3 items-start cursor-pointer">
                            <Checkbox checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                            <div className="flex flex-col gap-1">
                                <span className="text-content-strong text-sm">
                                    {t('webhook.form.showUnfinishedData')}
                                </span>
                                <span className="text-content-medium text-xs">
                                    {t('webhook.form.showUnfinishedDataDescription')}
                                </span>
                            </div>
                        </label>
                    )}
                />

                <div className="flex flex-col gap-3">
                    <span className="text-sm font-medium text-content-strong">{t('webhook.form.channels')}</span>
                    {form.watch('orderChannelIds').length === 0 &&
                        form.watch('orderConfirmationChannelIds').length === 0 &&
                        form.watch('rfqChannelIds').length === 0 &&
                        form.watch('invoiceChannelIds').length === 0 &&
                        form.watch('deliveryNoteChannelIds').length === 0 && (
                            <span className="text-xs text-content-medium">{t('webhook.form.channelsAllInfo')}</span>
                        )}

                    {orderChannels.length > 0 && (
                        <Controller
                            name="orderChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('webhook.form.orderChannels')}>
                                    <CheckboxGroup
                                        {...field}
                                        options={orderChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />
                    )}

                    {orderConfirmationChannels.length > 0 && (
                        <Controller
                            name="orderConfirmationChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('webhook.form.orderConfirmationChannels')}>
                                    <CheckboxGroup
                                        {...field}
                                        options={orderConfirmationChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />
                    )}

                    {rfqChannels.length > 0 && (
                        <Controller
                            name="rfqChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('webhook.form.rfqChannels')}>
                                    <CheckboxGroup
                                        {...field}
                                        options={rfqChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />
                    )}

                    {invoiceChannels.length > 0 && (
                        <Controller
                            name="invoiceChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('webhook.form.invoiceChannels')}>
                                    <CheckboxGroup
                                        {...field}
                                        options={invoiceChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />
                    )}

                    {deliveryNoteChannels.length > 0 && (
                        <Controller
                            name="deliveryNoteChannelIds"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('webhook.form.deliveryNoteChannels')}>
                                    <CheckboxGroup
                                        {...field}
                                        options={deliveryNoteChannels.map((c) => ({ value: c.id, label: c.name }))}
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />
                    )}
                </div>
            </form>
        </Modal>
    );
};

export const AddWebhookFormModal = () => {
    const { t } = useTranslation('integrations');
    const { refetch } = useOutletContext<WebhookFormModalOutletContext>();

    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const [createWebhook] = useMutation(CREATE_WEBHOOK);
    const handleCreate = async (formData) => {
        try {
            await createWebhook({
                variables: {
                    name: formData.name,
                    url: formData.url,
                    enabled: formData.enabled,
                    events: formData.events,
                    authType: formData.authType || null,
                    authCredentials: formData.authCredentials || null,
                    authHeader: formData.authHeader || null,
                    username: formData.username || null,
                    password: formData.password || null,
                    showUnfinishedData: formData.showUnfinishedData,
                    orderChannels: formData.orderChannelIds,
                    orderConfirmationChannels: formData.orderConfirmationChannelIds,
                    rfqChannels: formData.rfqChannelIds,
                    invoiceChannels: formData.invoiceChannelIds,
                    deliveryNoteChannels: formData.deliveryNoteChannelIds,
                },
            });
            await refetch();
            publishToast({ description: t('webhook.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_INTEGRATION_WEBHOOK_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('webhook.createError'), status: 'error' });
        }
    };

    return (
        <WebhookFormModal
            title={t('webhook.form.createTitle')}
            submitButtonLabel={t('webhook.form.createButton')}
            onSubmit={handleCreate}
            onClose={() => navigate(url(CUSTOMER_INTEGRATION_WEBHOOK_PATH))}
        />
    );
};

const WebhookRow = ({ integration, refetch }: { integration: any; refetch: any }) => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const [deleteWebhook] = useMutation(DELETE_WEBHOOK);
    const handleDelete = async () => {
        try {
            await deleteWebhook({ variables: { webhookId: integration.id } });
            await refetch();
            publishToast({ description: t('webhook.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('webhook.deleteError'), status: 'error' });
        }
    };

    const parsedEvents = JSON.parse(integration.events || '[]') as string[];
    const eventCount = parsedEvents.length;

    return (
        <Table.Row>
            <Table.Cell>{integration.name}</Table.Cell>
            <Table.Cell>
                <span className={integration.enabled ? 'text-content-success-strong' : 'text-content-medium'}>
                    {integration.enabled ? t('webhook.enabledStatus') : t('webhook.disabledStatus')}
                </span>
            </Table.Cell>
            <Table.Cell className="max-w-64 truncate">{integration.url}</Table.Cell>
            <Table.Cell>
                <span className="border-line-weak border border-solid bg-surface-strong px-1.5 rounded text-sm">
                    {eventCount === 0 ? t('webhook.allEvents') : `${eventCount} ${t('webhook.eventsLabel')}`}
                </span>
            </Table.Cell>
            <Table.Cell>{new Date(integration.createdAt).toLocaleString()}</Table.Cell>
            <Table.Cell sticky="right">
                <div className="flex gap-2">
                    <RowControlIconButton asChild>
                        <Link
                            to={url(CUSTOMER_INTEGRATION_WEBHOOK_EDIT_PATH, { integrationId: integration.id })}
                            className="!text-content-strong hover:!text-content-strong"
                        >
                            <EditIcon />
                        </Link>
                    </RowControlIconButton>

                    <RowControlIconButton
                        className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                        onClick={handleDelete}
                    >
                        <DeleteIcon />
                    </RowControlIconButton>
                </div>
            </Table.Cell>
        </Table.Row>
    );
};

const WebhookPage = () => {
    const { t } = useTranslation('integrations');

    const {
        data: data,
        loading: loading,
        refetch: refetch,
    } = useQuery(GET_WEBHOOKS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations = data?.webhooks || [];

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle className="flex items-center gap-2">
                        <span>{t('webhook.title')}</span>
                    </Page.HeaderTitle>

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_INTEGRATION_WEBHOOK_ADD_PATH)}>
                                <AddIcon />
                                {t('webhook.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="min-w-64 w-64">{t('webhook.table.name')}</Table.HeadCell>
                                <Table.HeadCell>{t('webhook.table.enabled')}</Table.HeadCell>
                                <Table.HeadCell>{t('webhook.table.url')}</Table.HeadCell>
                                <Table.HeadCell>{t('webhook.table.events')}</Table.HeadCell>
                                <Table.HeadCell>{t('webhook.table.createdAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations && integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <WebhookRow key={integration.id} integration={integration} refetch={refetch} />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={6} className="text-center text-content-medium">
                                        {t('webhook.noWebhooks')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>
                </Page.Content>
            </Page>

            <Outlet context={{ integrations, refetch }} />
        </Layout>
    );
};

export default WebhookPage;
